import React from 'react';

const EnglishPage = () => {
    return (
        <div>
            <h3>
                <span lang="en">Dear University Teacher!</span>
            </h3>
            <h3>
                <span lang="en">
                    Welcome using the <i>Reflection Compass</i> in research ethics and integrity
                    teaching!
                </span>
            </h3>
            <p>
                <span lang="en">
                    The <i>Reflection Compass</i> application is designed to support teachers in
                    teaching about research ethics and integrity, and to support students in
                    reflective engagement with research ethics/integrity learning tasks.
                </span>
            </p>

            <p>
                <span lang="en">
                    Many courses have elements of research ethics and integrity integrated in them –
                    like research seminars, methodology courses, academic writing, peer reviewing or
                    publishing seminars. In addition, reflection is an important aspect of becoming
                    ethically sensitive. Research ethics and reflection skills may be challenging to
                    evaluate pertaining if and what the students are learning. Moreover, students
                    may rely on AI tools to get assignments done – which is often fine, but if done
                    without own thinking and reflection, it is questionable what students have truly
                    learned. Teachers may wish they could avoid unwanted use of AI and maintain a
                    connection with the students’ learning process without being overloaded with
                    assignments to read.{' '}
                </span>
            </p>

            <h3>
                <span lang="en">What does the application do?</span>
            </h3>

            <p>
                <span lang="en">
                    We are introducing a research-based tool for university teachers who cover
                    research ethics/integrity in their teaching. The application is developed by
                    research ethics experts and pedagogues to help teachers to
                </span>
            </p>

            <p>
                <span lang="en">
                    ·<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </span>
                <span lang="en">
                    find out what the students are learning and if their teaching is effective;
                </span>
            </p>

            <p>
                <span lang="en">
                    ·<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </span>
                <span lang="en">
                    monitor how their students understand the ethics/integrity-related content
                    covered in a course;
                </span>
            </p>

            <p>
                <span lang="en">
                    ·<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </span>
                <span lang="en">
                    implement formative assessment and provide feedback to students during a course;
                </span>
            </p>

            <p>
                <span lang="en">
                    ·<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </span>
                <span lang="en">
                    encourage students to think about their learning and support the development of
                    students’ reflection skills;
                </span>
            </p>

            <p>
                <span lang="en">
                    ·<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </span>
                <span lang="en">
                    support students in learning to evaluate their own understanding.
                </span>
            </p>

            <h3>
                <span lang="en">How does the application work?</span>
            </h3>

            <p>
                <span lang="en">
                    The application guides the teacher to set up a course with assignments of topics
                    that require student reflection. After setting up the course and assignments (it
                    is a good idea to include several assignments in one course, the assignment time
                    will indicate when students can do them), the teacher then copies an invitation
                    to be shared with students. Teachers can also duplicate their courses and
                    assignments and use them in the coming semesters (courses will have unique IDs
                    that will help distinguish between different years and groups).
                </span>
            </p>

            <p>
                <span lang="en">
                    Students sign in using their credentials and can go to the assignment that the
                    teacher has indicated in the invitation link.  Students do two tasks – they
                    first reflect on the topic indicated in the assignment by writing a paragraph
                    following the task 1 description. After that they read five statements and pick
                    the one that best describes their understanding of the topic they had just
                    reflected on. After submitting their response, they will see automated feedback
                    describing the level of understanding they had picked. The application
                    encourages students to think if the reflection they submitted matches the level
                    of understanding described in the feedback. Students have a chance to go back to
                    their response and edit either their written paragraph or their level of
                    understanding.
                </span>
            </p>

            <p>
                <span lang="en">
                    The application analyses the indicated levels of understanding and provides
                    results as a pie-chart to the teacher (see figure 1).{' '}
                </span>
            </p>

            <p align="center">
                <span lang="en">
                    <img
                        alt="Figure 1. Pie-chart with the assignment results."
                        width="500"
                        height="auto"
                        src="/image003.png"
                    />
                </span>
            </p>

            <p>
                <span lang="en">Figure 1. Pie-chart with the assignment results.</span>
            </p>

            <p>
                <span lang="en">
                    Moreover, both the teacher and students can see learning progress on the linear
                    graph (see figure 2).{' '}
                </span>
            </p>

            <p align="center">
                <span lang="en">
                    <img
                        alt="Figure 2. Linear graph with student progress in two assignments."
                        width="700"
                        height="auto"
                        src="/image004.png"
                    />
                </span>
            </p>

            <p>
                <span lang="en">
                    Figure 2. Linear graph with student progress in two assignments.
                </span>
            </p>

            <p>
                <span lang="en">
                    In addition to the automated feedback, the teacher can also provide more
                    personalised feedback in the form of written text or by indicating the teacher’s
                    estimation of the level of understanding displayed in the paragraph written by
                    the student.{' '}
                </span>
            </p>

            <p>
                <span lang="en">
                    While using the app mainly supports students’ reflective learning and formative
                    assessment during the course, the graph displaying the individual student’s
                    learning process serves well as a foundation for a subsequent ‘reflection
                    journal’. The graph functions as a prompt for students as they summarise their
                    learning process in the reflection journal. The reflection journal can be used
                    for the purpose of summative assessment, that is, as a graded assignment
                    contributing to the final assessment in the course, usually the grade.{' '}
                </span>
            </p>

            <p>
                <span lang="en">
                    Repeated use of the application provides teachers insights on how the students
                    are progressing, which topics seem to be more challenging and which ones have
                    been mastered. Students can also see their own progress on the graph, and by
                    reading the feedback provided by the application and the teacher, can work on
                    their reflection skills.
                </span>
            </p>

            <p>
                <span lang="en">&nbsp;</span>
            </p>

            <p>
                <span lang="en">Levels of understanding based on the SOLO taxonomy:</span>
            </p>

            <p>
                <span lang="en">Symbol</span>
                <span lang="en">  </span>
                <span lang="FI">
                    <img alt="Need help to understand" width="12" height="12" src="/image005.png" />
                </span>
                <span lang="en">  </span>
                <span lang="en">
                    indicates a need for help to understand. To improve their level of understanding
                    the learner can improve their ethical competence by consulting relevant codes of
                    conduct, and to discuss with experienced researchers how they think about making
                    right choices in research.
                </span>
            </p>

            <p>
                <span lang="en">Symbol</span>
                <span lang="en"> </span>
                <span lang="en">
                    <img alt="Emerging understanding" width="13" height="13" src="/image006.png" />
                </span>
                <span lang="en">  </span>
                <span lang="en">
                    indicates emerging understanding. The learner is encouraged to think of related
                    questions, or new questions which may follow the initial issue, it is a good
                    idea to map who are concerned by a situation, and what kind of different
                    questions they would raise about the situation.
                </span>
            </p>

            <p>
                <span lang="en">Symbol</span>
                <span lang="en"> </span>
                <span lang="en">
                    <img alt="Moderate understanding" width="17" height="17" src="/image007.png" />
                </span>
                <span lang="en"> </span>
                <span lang="en">
                    indicates moderate understanding. The learner should try to see connections
                    between different ethical aspects in research and think of examples to describe
                    those connections.
                </span>
            </p>

            <p>
                <span lang="en">Symbol</span>
                <span lang="en"></span>
                <span lang="en">
                    <img alt="Coherent understanding" width="16" height="16" src="/image008.png" />
                </span>
                <span lang="en"> </span>
                <span lang="en">
                    indicates coherent understanding in which relationships between concepts are
                    understood. The learner is advised to apply their knowledge in various contexts
                    and provide help to others.
                </span>
            </p>

            <p>
                <span lang="en">Symbol</span>
                <span lang="en">
                     {' '}
                    <img alt="In-depth understanding" width="19" height="19" src="/image009.png" />
                      
                </span>
                <span lang="en">
                    indicates in-depth understanding. The learner may have thought about themselves
                    as a role model in promoting good scientific practice in the research community
                    more broadly, and in helping to create an ethically sustainable research
                    culture.
                </span>
            </p>

            <h3>
                <span lang="en">What background is the application based on?</span>
            </h3>

            <p>
                <i>
                    <span lang="en">Reflection Compass</span>
                </i>
                <span lang="en">
                    {' '}
                    is built on research and design principles and utilizes the{' '}
                    <i>Structure of Observed Learning Outcomes</i> (SOLO) taxonomy to provide solid
                    grounding to the results. By combining the results of the application,
                    monitoring the student work in class and work submitted at different timepoints
                    of the course, the teacher can get a holistic picture of the student development
                    and effectiveness of the training.
                </span>
            </p>

            <p>
                <span lang="en">
                    If you are interested in finding out more about the SOLO taxonomy or the
                    research behind the application, please visit the following links:
                </span>
            </p>

            <p>
                <span lang="en">
                    SOLO taxonomy:{' '}
                    <a href="https://www.johnbiggs.com.au/academic/solo-taxonomy/">
                        https://www.johnbiggs.com.au/academic/solo-taxonomy/
                    </a>
                </span>
            </p>

            <p>
                <span lang="en">
                    Article: John Biggs (1999) What the Student Does: teaching for enhanced
                    learning, Higher Education Research &amp; Development, 18:1, 57-75, DOI:
                    10.1080/0729436990180105
                </span>
            </p>

            <p>
                <span lang="en">
                    Link to the article:{' '}
                    <a href="https://www.tandfonline.com/doi/pdf/10.1080/0729436990180105">
                        https://www.tandfonline.com/doi/pdf/10.1080/0729436990180105
                    </a>
                </span>
            </p>

            <p>
                <i>
                    <span lang="en">
                        Research about the development of the reflection application:
                    </span>
                </i>
            </p>

            <p>
                <span lang="en">
                    Article: Anu Tammeleht &amp; Erika Löfström (2024) Learners’ self-assessment as
                    a measure to evaluate the effectiveness of research ethics and integrity
                    training: can we rely on self-reports?, <i>Ethics &amp; Behavior</i>, 34:8,
                    575-596, DOI: 10.1080/10508422.2023.2266073{' '}
                </span>
            </p>

            <p>
                <span lang="en">
                    Link to the article:{' '}
                    <a href="https://www.tandfonline.com/doi/pdf/10.1080/10508422.2023.2266073">
                        https://www.tandfonline.com/doi/pdf/10.1080/10508422.2023.2266073
                    </a>
                </span>
            </p>

            <p>
                <span lang="en">The development of the application was supported by:</span>
            </p>

            <p>
                <span lang="en">
                    <img alt="Beyond apples logo" width="214" height="auto" src="/image011.png" />
                </span>
                <span lang="en">
                     {' '}
                    <img alt="European union logo" width="117" height="auto" src="/image012.png" />
                       
                </span>
                <span lang="en">
                    <img
                        border="0"
                        width="200"
                        height="auto"
                        src="/image013.png"
                        alt="Reimagining Teachers and Teacher Education for our Futures | University of  Helsinki"
                    />
                </span>
            </p>
        </div>
    );
};

export default EnglishPage;
